'use client';

import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import heroMobileCreative from '@/assets/hero-mobile-creative.webp';
import { usePageTracking } from '@/hooks/usePageTracking';

export function Hero() {
  usePageTracking();
  
  const [isMuted, setIsMuted] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [heroVideoSrc, setHeroVideoSrc] = useState<string | null>(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Detect mobile for performance optimization
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Dynamically load video only on desktop
  useEffect(() => {
    if (!isMobile) {
      import('@/assets/robot-tasks-video.mp4').then((mod) => {
        setHeroVideoSrc(mod.default);
      }).catch(() => {
        setVideoFailed(true);
      });
    }
  }, [isMobile]);

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50); // Show background after 50px scroll
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ensure video is muted immediately on load to prevent any audio
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = 0;
      videoRef.current.muted = true;
      videoRef.current.defaultMuted = true;

      // Force mute on play
      videoRef.current.addEventListener('play', () => {
        if (videoRef.current) {
          videoRef.current.muted = isMuted;
          videoRef.current.volume = isMuted ? 0 : 0.7;
        }
      });

      // Try to play the video
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Silent fail - autoplay blocked is expected behavior
        });
      }
    }
  }, []);

  // Update video mute state when isMuted changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
      videoRef.current.volume = isMuted ? 0 : 0.7;
    }
  }, [isMuted]);

  // Handle body scroll lock when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    if (isMobileMenuOpen) {
      window.addEventListener('scroll', handleScroll);
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobileMenuOpen]);
  return <div className="relative min-h-screen h-screen w-full overflow-hidden bg-black">
      {/* Background fallback while video loads */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800" />
      
      {/* MASSIVE VIDEO - Full screen background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {isMobile ? (
          <div className="w-full h-full relative overflow-hidden">
            <img
              src={heroMobileCreative}
              alt="Tecnologia e automação com inteligência artificial"
              className="w-full h-full object-cover animate-[slowZoom_20s_ease-in-out_infinite_alternate]"
              width={768}
              height={1024}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20" />
          </div>
        ) : heroVideoSrc && !videoFailed ? (
          <video 
            ref={videoRef} 
            src={heroVideoSrc} 
            className="w-full h-full object-cover scale-110" 
            autoPlay 
            muted 
            loop 
            playsInline 
            preload="metadata"
            onError={(e) => {
              console.warn('Video failed to load');
              e.currentTarget.style.display = 'none';
              setVideoFailed(true);
            }}
            width={1920}
            height={1080}
            aria-label="Vídeo de fundo mostrando tecnologia e automação com inteligência artificial"
          />
        ) : (
          <div className="w-full h-full relative overflow-hidden">
            <img
              src={heroMobileCreative}
              alt="Tecnologia e automação com inteligência artificial"
              className="w-full h-full object-cover"
              width={1920}
              height={1080}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20" />
          </div>
        )}
      </div>

      {/* Full-Width Navbar - always visible, animations only on desktop */}
      <motion.nav 
        initial={{ opacity: 1, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        className="fixed top-0 left-0 right-0 w-full z-[110]"
      >
        <div className={`w-full px-6 sm:px-8 lg:px-12 py-4 transition-all duration-300 ease-out ${isScrolled ? 'bg-black/80 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'}`}>
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div whileHover={{
            scale: 1.05
          }} className="flex items-center cursor-pointer" onClick={() => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }}>
              <span className="text-white font-bold text-2xl tracking-tight">Odran Solutions</span>
            </motion.div>

            {/* Navigation Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#portfolio" className="text-white hover:text-white/80 font-medium gentle-animation hover:scale-105">
                Portfólio
              </a>
              <a href="#about" className="text-white hover:text-white/80 font-medium gentle-animation hover:scale-105">
                Processo
              </a>
              <a href="#services" className="text-white hover:text-white/80 font-medium gentle-animation hover:scale-105">
                Serviços
              </a>
              <a href="/agentes-ia" className="text-white hover:text-white/80 font-medium gentle-animation hover:scale-105">
                Agentes de IA
              </a>
              <a href="#blog" className="text-white hover:text-white/80 font-medium gentle-animation hover:scale-105">
                Blog
              </a>
              <a href="#faq" className="text-white hover:text-white/80 font-medium gentle-animation hover:scale-105">
                FAQ
              </a>
              <a href="#contact" className="text-white hover:text-white/80 font-medium gentle-animation hover:scale-105">
                Contato
              </a>
            </div>

            {/* Right Side - CTA + Mobile Menu */}
            <div className="flex items-center space-x-3 relative">
              
              {/* WhatsApp Button */}
              <a
                href="https://w.app/odransolutions"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Fale conosco pelo WhatsApp"
                className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#25D366] hover:bg-[#20BD5A] shadow-md transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="white" className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </a>

              {/* CTA Button - Visible on all screens */}
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={() => {
              const contactSection = document.getElementById('contact');
              contactSection?.scrollIntoView({
                behavior: 'smooth'
              });
            }} className="bg-red-600 backdrop-blur-sm text-white font-semibold px-4 sm:px-6 py-2 sm:py-3 rounded-md hover:bg-red-700 gentle-animation cursor-pointer text-sm sm:text-base">
                Agendar Reunião
              </motion.button>

              {/* Mobile Hamburger Menu Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
                className="md:hidden glass-effect p-3 rounded-full text-white hover:bg-white/20 active:bg-white/30 gentle-animation cursor-pointer z-[120] relative"
                aria-label={isMobileMenuOpen ? "Fechar menu de navegação" : "Abrir menu de navegação"}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu-panel"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-md z-[80] cursor-pointer" onClick={() => setIsMobileMenuOpen(false)} />}

      {/* Mobile Menu Panel */}
      <motion.div 
        id="mobile-menu-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Menu de navegação móvel"
        initial={{
          x: '100%'
        }} 
        animate={{
          x: isMobileMenuOpen ? '0%' : '100%'
        }} 
        transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} 
        className="md:hidden fixed top-0 right-0 h-full w-72 max-w-[85vw] bg-black/90 backdrop-blur-xl border-l border-white/10 z-[90] mobile-menu-panel pointer-events-auto" 
        onClick={e => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Close Button at the top */}
          <div className="flex justify-end p-4">
            <button 
              onClick={() => setIsMobileMenuOpen(false)} 
              className="glass-effect p-3 rounded-full text-white hover:bg-white/20 active:bg-white/30 gentle-animation cursor-pointer"
              aria-label="Fechar menu de navegação"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex flex-col px-6 pb-6 h-full">
            {/* Mobile Navigation Links */}
            <div className="flex flex-col space-y-4 text-white">
              <a href="#portfolio" className="mobile-menu-link px-4 py-3 hover:text-white/80 hover:bg-white/10 rounded-lg gentle-animation font-medium text-lg active:bg-white/20" onClick={() => setIsMobileMenuOpen(false)}>
                Portfólio
              </a>
              <a href="#about" className="mobile-menu-link px-4 py-3 hover:text-white/80 hover:bg-white/10 rounded-lg gentle-animation font-medium text-lg active:bg-white/20" onClick={() => setIsMobileMenuOpen(false)}>
                Processo
              </a>
              <a href="#services" className="mobile-menu-link px-4 py-3 hover:text-white/80 hover:bg-white/10 rounded-lg gentle-animation font-medium text-lg active:bg-white/20" onClick={() => setIsMobileMenuOpen(false)}>
                Serviços
              </a>
              <a href="/agentes-ia" className="mobile-menu-link px-4 py-3 hover:text-white/80 hover:bg-white/10 rounded-lg gentle-animation font-medium text-lg active:bg-white/20" onClick={() => setIsMobileMenuOpen(false)}>
                Agentes de IA
              </a>
              <a href="#blog" className="mobile-menu-link px-4 py-3 hover:text-white/80 hover:bg-white/10 rounded-lg gentle-animation font-medium text-lg active:bg-white/20" onClick={() => setIsMobileMenuOpen(false)}>
                Blog
              </a>
              <a href="#faq" className="mobile-menu-link px-4 py-3 hover:text-white/80 hover:bg-white/10 rounded-lg gentle-animation font-medium text-lg active:bg-white/20" onClick={() => setIsMobileMenuOpen(false)}>
                FAQ
              </a>
              <a href="#contact" className="mobile-menu-link px-4 py-3 hover:text-white/80 hover:bg-white/10 rounded-lg gentle-animation font-medium text-lg active:bg-white/20" onClick={() => setIsMobileMenuOpen(false)}>
                Contato
              </a>
            </div>

            {/* Mobile CTA Button */}
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => {
            const contactSection = document.getElementById('contact');
            contactSection?.scrollIntoView({
              behavior: 'smooth'
            });
            setIsMobileMenuOpen(false);
          }} className="bg-red-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-red-700 active:bg-red-800 gentle-animation mt-8 cursor-pointer">
              Agendar Reunião
            </motion.button>
          </div>
        </div>
      </motion.div>



      {/* Big Studio Title - Lower Left - always visible */}
      <div 
        className="absolute bottom-12 left-6 sm:left-8 lg:left-12 z-40"
      >
        <div className="max-w-3xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[0.95] text-white uppercase tracking-tight">
            <span className="block">CRIAMOS</span>
            <span className="block">AGENTES DE IA</span>
            <span className="block text-red-500">QUE TRANSFORMAM</span>
            <span className="block">NEGÓCIOS</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl max-w-xl text-white">
            Estratégias de marketing, agentes de IA, tráfego pago e sites que geram clientes e escalam seu negócio.
          </p>
          
          {/* CTA Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 rounded-lg text-lg transition-colors cursor-pointer shadow-lg shadow-red-600/30"
            >
              Agendar Reunião
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="border-2 border-white/40 hover:border-white/70 text-white font-semibold px-8 py-4 rounded-lg text-lg backdrop-blur-sm hover:bg-white/10 transition-all cursor-pointer"
            >
              Ver Portfólio
            </motion.button>
          </div>
        </div>
      </div>


    </div>;
}